/**
 * LOT 411 — Click-to-edit WYSIWYG primitives
 * ───────────────────────────────────────────
 * Wraps any public-page text/field with inline edit capabilities when the
 * page is rendered inside the SiteEditor iframe in edit mode.
 *
 * Detection:
 *   • The page is inside an iframe AND the URL carries `?_cms_edit=1`
 *
 * Behaviour:
 *   • Edit mode OFF (production / public viewing): renders a plain element,
 *     preserving className/tag.
 *   • Edit mode ON:
 *       - Hover shows a bronze outline + small badge with the field path
 *       - Click switches to contenteditable; blur sends new value to the
 *         parent editor via `postMessage` `{ type: 'cms:edit-change',
 *         path, value }`. Parent updates the in-memory draft + re-sends
 *         `cms:apply-draft` so other editors see the change too.
 *       - Multiline text gets `whiteSpace: pre-wrap` and Enter inserts <br>
 *         instead of submitting.
 *
 * Origin checks are strict: only same-origin messages are honoured.
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

export function isInCmsEditMode() {
  if (typeof window === 'undefined') return false;
  try {
    const isFramed = window.self !== window.top;
    const hasFlag = /(?:^|[?&])_cms_edit=1/.test(window.location.search);
    return isFramed && hasFlag;
  } catch {
    return /(?:^|[?&])_cms_edit=1/.test(window.location.search);
  }
}

function sendToParent(payload) {
  try {
    window.parent?.postMessage(payload, window.location.origin);
  } catch {
    /* ignore */
  }
}

/**
 * <CmsEditable
 *   path="sections.header.title"
 *   value={cmsTitle}
 *   defaultValue={t('about.title')}
 *   as="h1"
 *   className="font-heading text-4xl"
 *   multiline={false}
 * />
 *
 * `value` is the live (CMS or draft) value; `defaultValue` is the i18n
 * fallback shown when value is empty. In edit mode both inputs are still
 * shown — the editor sees the actual text rendered to public visitors.
 */
export function CmsEditable({
  path,
  value,
  defaultValue,
  as: Tag = 'span',
  className = '',
  multiline = false,
  testid,
  children,
  ...rest
}) {
  const editMode = useMemo(() => isInCmsEditMode(), []);
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  // Resolved text actually rendered. Either the live value or the i18n fallback.
  const displayText = (value && String(value).trim()) || defaultValue || '';

  // Listen for draft updates so multi-occurrence fields stay in sync.
  useEffect(() => {
    if (!editMode) return undefined;
    function onMessage(e) {
      if (e.origin !== window.location.origin) return;
      const data = e.data || {};
      if (data && data.type === 'cms:apply-draft-field' && data.path === path) {
        if (ref.current && ref.current.innerText !== data.value) {
          ref.current.innerText = data.value;
        }
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [editMode, path]);

  const handleFocus = useCallback(() => {
    setEditing(true);
    sendToParent({ type: 'cms:edit-focus', path });
  }, [path]);

  const handleBlur = useCallback((e) => {
    setEditing(false);
    const newVal = (e.target.innerText || '').trim();
    if (newVal !== (displayText || '').trim()) {
      sendToParent({ type: 'cms:edit-change', path, value: newVal });
    }
  }, [displayText, path]);

  const handleKeyDown = useCallback((e) => {
    // Submit on Enter when single-line
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
    // Esc to cancel — restore original text
    if (e.key === 'Escape') {
      e.preventDefault();
      e.currentTarget.innerText = displayText;
      e.currentTarget.blur();
    }
  }, [multiline, displayText]);

  if (!editMode) {
    // Production / public viewing — render plain element
    return (
      <Tag
        className={className}
        data-cms-field={path}
        data-testid={testid}
        {...rest}
      >
        {children ?? displayText}
      </Tag>
    );
  }

  // Edit mode — render contenteditable
  return (
    <Tag
      ref={ref}
      className={`${className} cms-editable ${editing ? 'is-editing' : ''}`}
      contentEditable
      suppressContentEditableWarning
      spellCheck={false}
      data-cms-field={path}
      data-cms-editable="true"
      data-testid={testid}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {displayText}
    </Tag>
  );
}

/**
 * Convenience wrapper for short labels (CTAs, badges, overlines). Always
 * forces single-line behaviour and renders as a <span> by default. Use
 * <CmsEditable as="h1" multiline /> for paragraphs.
 */
export function CmsEditableShort(props) {
  return <CmsEditable multiline={false} {...props} />;
}

export default CmsEditable;
